import { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { cn } from '@/utils/cn';

export type AutomationMetricOption = {
  key: string;
  label: string;
};

export function filterAutomationMetrics<T extends AutomationMetricOption>(
  metrics: T[],
  query: string
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return metrics;
  return metrics.filter(
    (metric) => metric.label.toLowerCase().includes(q) || metric.key.toLowerCase().includes(q)
  );
}

export function AutomationMetricSelect({
  metrics,
  value,
  onChange,
}: {
  metrics: AutomationMetricOption[];
  value: string;
  onChange: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selected = metrics.find((metric) => metric.key === value);
  const visible = useMemo(() => filterAutomationMetrics(metrics, search), [metrics, search]);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex min-w-0 w-full items-center justify-between gap-1 rounded border border-border bg-surface px-2 py-1.5 text-left text-sm"
          aria-label="Condition field"
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className="truncate">{selected?.label || value || 'Select field'}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-foreground-muted" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="z-[90] w-[min(22rem,calc(100vw-2rem))] p-0">
        <div className="border-b border-border p-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search fields…"
            className="w-full rounded border border-border px-2 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
            autoFocus
            aria-label="Search condition fields"
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1" role="listbox">
          {visible.length === 0 ? (
            <p className="px-3 py-2 text-sm text-foreground-muted">No fields match.</p>
          ) : (
            visible.map((metric) => (
              <button
                key={metric.key}
                type="button"
                role="option"
                aria-selected={metric.key === value}
                className={cn(
                  'flex w-full px-3 py-1.5 text-left text-sm',
                  metric.key === value ? 'bg-brand/10 text-brand' : 'hover:bg-surface-muted'
                )}
                onClick={() => {
                  onChange(metric.key);
                  setOpen(false);
                  setSearch('');
                }}
              >
                {metric.label}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
